/**
 * Formatting DOCX Generator
 *
 * Generates professionally formatted DOCX documents matching the PDF format
 * specifications for wider audience consumption.
 *
 * DOCX Specifications:
 * - US Letter (8.5" x 11")
 * - Margins: ~0.83" left/right, ~1" top/bottom
 * - Font: Helvetica-equivalent (Calibri/Arial)
 * - Title Page with executive summary
 * - Numbered Parts structure
 * - Page numbers and copyright on every page
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Footer,
  PageNumber,
  PageBreak,
  AlignmentType,
  convertInchesToTwip,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ImageRun,
} from 'docx';

// Shared with the PDF builder on purpose: a PDF and a DOCX of the same
// translation that disagree about which page a clause is on is its own defect.
import { normalizeSourcePageMarkers, organizationNameForSentence, tidyDoubledPeriods } from './formattingPdf';

// ============================================================================
// Types (matching formattingPdf.ts)
// ============================================================================

/**
 * Translation overrides for creating translated DOCX documents.
 * When provided, these values replace the default English text.
 */
export interface FormattedDocxTranslationOverrides {
  /** Translated document title */
  title?: string;
  /** Translated date string (e.g., "15 de enero de 2024") */
  date?: string;
  /** Firm/organization name (typically kept as-is) */
  organizationName?: string;
  /** Translated full copyright notice */
  copyrightNotice?: string;
  /** Translated short copyright for footers */
  copyrightShort?: string;
}

export interface BuildFormattedDocxParams {
  /** The formatted report content from Claude */
  content: string;
  /** Original research query/topic for the title */
  title: string;
  /** Date of report creation */
  createdAt?: Date;
  /** Optional translation overrides for creating translated DOCX files */
  translations?: FormattedDocxTranslationOverrides;
  /**
   * Figures pulled out of the source PDF, keyed to the page they sat on.
   *
   * WHY (2026-08-20): the PDF builder has taken these since 2026-08-13 and the
   * DOCX builder was never given them — documentGenerator passed sourceImages
   * to one of its two deliverables. The 200-page manual shipped a 7.9MB PDF
   * with its charts and a 105KB Word file with none, from the same content.
   */
  sourceImages?: Array<{
    pageNumber: number;
    base64: string;
    widthPx: number;
    heightPx: number;
    afterCharRatio?: number;
  }>;
  /**
   * Emit the generated title page. Defaults to true.
   * False when the content IS a user-supplied document that already has its
   * own title page — see includeCoverPage in formattingPdf.ts
   * (2026-08-13 formatting feedback: "broken first page").
   */
  includeCoverPage?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const ORGANIZATION = 'The Law Offices of Fernando Hidalgo, Inc.';

// ============================================================================
// Content Cleaning (matching formattingPdf.ts)
// ============================================================================

/**
 * Strip internal thinking/reasoning blocks from AI responses.
 */
function stripThinkingBlocks(input: string): string {
  if (!input) return '';
  let cleaned = input;
  cleaned = cleaned.replace(/<think>[\s\S]*?<\/think>/gi, '');
  cleaned = cleaned.replace(/<\/?think>/gi, '');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned.trim();
}

/**
 * Strip markdown formatting while preserving text content.
 */
function stripMarkdownFormatting(input: string): string {
  let out = input || '';
  // Remove markdown links, keep label
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
  // Remove bold/italic markers
  out = out.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
  out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
  out = out.replace(/__([^_]+)__/g, '$1');
  out = out.replace(/\*([^*]+)\*/g, '$1');
  out = out.replace(/_([^_]+)_/g, '$1');
  // Remove backticks
  out = out.replace(/`+([^`]+)`+/g, '$1');
  return out.trim();
}

// ============================================================================
// Block Parsing (matching formattingPdf.ts structure)
// ============================================================================

type BlockKind = 'title' | 'part' | 'section' | 'subsection' | 'bullet' | 'numbered' | 'callout' | 'paragraph' | 'table' | 'pagebreak' | 'figure';

/**
 * Page marker written by our own PDF text extractor ("--- Page N ---").
 * Kept in lockstep with SOURCE_PAGE_MARKER in formattingPdf.ts — the DOCX and
 * the PDF must paginate a translated document identically, or the two
 * deliverables disagree about what page a clause is on.
 * See INCIDENT-20260813 (formatting fidelity).
 */
const SOURCE_PAGE_MARKER = /^\s*-{2,}\s*[\p{L}]+\s+(\d+)\s*-{2,}\s*$/u;

interface Block {
  kind: BlockKind;
  text: string;
  /** Table data (only for kind === 'table') */
  tableRows?: string[][];
  /** Source page this block belongs to, for figure placement. */
  sourcePage?: number;
  /** Figure payload (only for kind === 'figure'). */
  figure?: { base64: string; widthPx: number; heightPx: number };
}

/**
 * Parse a markdown table row (line starting with |) into cells.
 */
function parseTableRow(line: string): string[] {
  return line
    .split('|')
    .map(cell => cell.trim())
    .filter((cell, idx, arr) => {
      // Remove empty first/last cells created by | at the start/end of line
      if (idx === 0 && cell === '') return false;
      if (idx === arr.length - 1 && cell === '') return false;
      return true;
    })
    .map(cell => stripMarkdownFormatting(cell));
}

/**
 * Check if a line is a markdown table separator (contains only -, :, |, and spaces).
 */
function isTableSeparator(line: string): boolean {
  return /^[\s|:-]+$/.test(line) && line.includes('-');
}

function parseBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  const lines = (content || '').split('\n');
  let tableBuffer: string[] = [];

  // Helper to flush accumulated table rows into a table block
  const flushTable = () => {
    if (tableBuffer.length === 0) return;

    // Parse table rows
    const hasSeparator = tableBuffer.length > 1 && isTableSeparator(tableBuffer[1]);
    const dataRows = hasSeparator ? [tableBuffer[0], ...tableBuffer.slice(2)] : tableBuffer;
    const tableRows = dataRows
      .filter(row => !isTableSeparator(row))
      .map(parseTableRow)
      .filter(row => row.length > 0);

    if (tableRows.length > 0) {
      blocks.push({
        kind: 'table',
        text: '',
        tableRows,
      });
    }
    tableBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushTable();
      continue;
    }

    // Detect markdown table rows (lines starting with |)
    if (line.startsWith('|')) {
      tableBuffer.push(line);
      continue;
    }

    // Not a table line - flush any pending table
    flushTable();

    // Source page marker -> real page break, never body text.
    const pageMarker = line.match(SOURCE_PAGE_MARKER);
    if (pageMarker) {
      blocks.push({ kind: 'pagebreak', text: '', sourcePage: Number(pageMarker[1]) || undefined });
      continue;
    }

    // Detect Part headers: "Part X:" or "**Part X:**" at start
    const partMatch = line.match(/^\*?\*?Part\s+(\d+)[:.]?\*?\*?\s*(.*)$/i);
    if (partMatch) {
      const partTitle = partMatch[2]
        ? `Part ${partMatch[1]}: ${stripMarkdownFormatting(partMatch[2])}`
        : `Part ${partMatch[1]}`;
      blocks.push({ kind: 'part', text: partTitle });
      continue;
    }

    // Detect title (# at start, single hash)
    if (line.startsWith('# ') && !line.startsWith('## ')) {
      blocks.push({ kind: 'title', text: stripMarkdownFormatting(line.slice(2)) });
      continue;
    }

    // Detect section headers (## at start)
    if (line.startsWith('## ')) {
      blocks.push({ kind: 'section', text: stripMarkdownFormatting(line.slice(3)) });
      continue;
    }

    // Detect subsection headers (### at start)
    if (line.startsWith('### ')) {
      blocks.push({ kind: 'subsection', text: stripMarkdownFormatting(line.slice(4)) });
      continue;
    }

    // Detect callout boxes (Important:, Critical:, Example:, Note:, Warning:)
    const calloutMatch = line.match(/^\*?\*?(Important|Critical|Example|Note|Warning):\*?\*?\s*(.*)$/i);
    if (calloutMatch) {
      const label = calloutMatch[1];
      const text = calloutMatch[2] || '';
      blocks.push({ kind: 'callout', text: `${label}: ${stripMarkdownFormatting(text)}` });
      continue;
    }

    // Detect bullet points (- or * at start)
    if (/^[-*]\s+/.test(line)) {
      blocks.push({ kind: 'bullet', text: stripMarkdownFormatting(line.replace(/^[-*]\s+/, '')) });
      continue;
    }

    // Detect numbered lists
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      blocks.push({ kind: 'numbered', text: `${numMatch[1]}. ${stripMarkdownFormatting(numMatch[2])}` });
      continue;
    }

    // Default: paragraph
    blocks.push({ kind: 'paragraph', text: stripMarkdownFormatting(line) });
  }

  // Flush any remaining table
  flushTable();

  return blocks;
}

// ============================================================================
// DOCX Builder
// ============================================================================

/** Content width available to a figure, in points (Letter minus 1in margins). */
const DOCX_FIGURE_MAX_WIDTH_PT = 468;
/** Tallest a single figure may be, so one chart cannot own a whole page. */
const DOCX_FIGURE_MAX_HEIGHT_PT = 520;

/**
 * Turn one source figure into a centred image paragraph, scaled to fit.
 *
 * Fail-safe: a figure that cannot be decoded is skipped, never thrown. Missing
 * artwork is a blemish; a builder that throws costs the whole deliverable, and
 * the translated text is the irreplaceable part.
 */
function figureParagraph(
  figure?: { base64: string; widthPx: number; heightPx: number },
): Paragraph | null {
  if (!figure?.base64 || !figure.widthPx || !figure.heightPx) return null;
  try {
    const scale = Math.min(
      DOCX_FIGURE_MAX_WIDTH_PT / figure.widthPx,
      DOCX_FIGURE_MAX_HEIGHT_PT / figure.heightPx,
      1,
    );
    const width = Math.max(1, Math.round(figure.widthPx * scale));
    const height = Math.max(1, Math.round(figure.heightPx * scale));
    return new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 160, after: 160 },
      children: [
        new ImageRun({
          data: Uint8Array.from(atob(figure.base64), (c) => c.charCodeAt(0)),
          transformation: { width, height },
        }),
      ],
    });
  } catch {
    return null;
  }
}

/**
 * Splice each source figure into the block stream at its place in the flow.
 *
 * Figures carry afterCharRatio — the fraction of their source page's text that
 * appeared ABOVE them — because the deliverable reflows and an absolute
 * coordinate would be meaningless in another language. This mirrors what the
 * PDF builder does with the same number, so the two deliverables agree about
 * where a chart belongs.
 */
function placeFiguresInBlocks(
  blocks: Block[],
  sourceImages?: BuildFormattedDocxParams['sourceImages'],
): Block[] {
  const figures = sourceImages || [];
  if (figures.length === 0) return blocks;

  const byPage = new Map<number, typeof figures>();
  for (const figure of figures) {
    const page = Number(figure.pageNumber) || 1;
    if (!byPage.has(page)) byPage.set(page, []);
    byPage.get(page)!.push(figure);
  }
  for (const list of byPage.values()) {
    list.sort((a, b) => (a.afterCharRatio ?? 1) - (b.afterCharRatio ?? 1));
  }

  // Which source page each block belongs to, and how much text each page holds.
  let page = 1;
  const pageOf: number[] = [];
  const charsOnPage = new Map<number, number>();
  blocks.forEach((block) => {
    if (block.kind === 'pagebreak') page = block.sourcePage ?? page + 1;
    pageOf.push(page);
    charsOnPage.set(page, (charsOnPage.get(page) || 0) + (block.text || '').length);
  });

  const out: Block[] = [];
  const drawn = new Set<typeof figures[number]>();
  let seenOnPage = 0;
  blocks.forEach((block, idx) => {
    const blockPage = pageOf[idx];
    if (idx === 0 || pageOf[idx - 1] !== blockPage) seenOnPage = 0;
    out.push(block);
    seenOnPage += (block.text || '').length;
    const total = charsOnPage.get(blockPage) || 0;
    const ratio = total > 0 ? seenOnPage / total : 1;
    const isLastOfPage = idx === blocks.length - 1 || pageOf[idx + 1] !== blockPage;
    for (const figure of byPage.get(blockPage) || []) {
      if (drawn.has(figure)) continue;
      // Placed once its share of the page's text has gone by — or, for
      // anything still pending, before the page ends, so no figure is lost.
      if (ratio >= (figure.afterCharRatio ?? 1) || isLastOfPage) {
        drawn.add(figure);
        out.push({
          kind: 'figure',
          text: '',
          sourcePage: blockPage,
          figure: { base64: figure.base64, widthPx: figure.widthPx, heightPx: figure.heightPx },
        });
      }
    }
  });

  // Figures whose page never appeared in the content still belong in the file.
  for (const figure of figures) {
    if (drawn.has(figure)) continue;
    out.push({
      kind: 'figure',
      text: '',
      sourcePage: figure.pageNumber,
      figure: { base64: figure.base64, widthPx: figure.widthPx, heightPx: figure.heightPx },
    });
  }
  return out;
}

export async function buildFormattedDocx(params: BuildFormattedDocxParams): Promise<File> {
  const createdAt = params.createdAt || new Date();
  const year = createdAt.getFullYear();
  const translations = params.translations || {};

  // Use translated values if provided, otherwise use defaults
  const organizationName = translations.organizationName || ORGANIZATION;
  const dateStr =
    translations.date ||
    createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const orgForSentence = organizationNameForSentence(organizationName);
  const copyrightNotice = tidyDoubledPeriods(
    translations.copyrightNotice ||
    `(c) ${year} ${orgForSentence}. Generated by Prest0n. Facilitated by ${orgForSentence}. All rights reserved.`,
  );
  const copyrightShort = tidyDoubledPeriods(
    translations.copyrightShort || `(c) ${year} ${orgForSentence}. All rights reserved.`,
  );

  const displayTitle = translations.title || params.title || 'Formatted Legal Report';

  // Strip any thinking blocks that might have slipped through
  const cleanedContent = normalizeSourcePageMarkers(stripThinkingBlocks(params.content));
  const blocks = parseBlocks(cleanedContent);

  // Build title page content
  const titlePageChildren: Paragraph[] = [
    // Add spacing before title
    new Paragraph({ spacing: { before: 2000 } }),

    // Document title (centered, bold, large)
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: displayTitle,
          bold: true,
          size: 36, // 18pt
          font: 'Arial',
        }),
      ],
    }),

    // Date (centered, smaller, gray)
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 1600 },
      children: [
        new TextRun({
          text: dateStr,
          size: 20, // 10pt
          font: 'Arial',
          color: '666666',
        }),
      ],
    }),

    // Copyright notice (centered, italic, small, gray)
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: copyrightNotice,
          italics: true,
          size: 16, // 8pt
          font: 'Arial',
          color: '808080',
        }),
      ],
    }),
  ];

  // Build content paragraphs and tables
  const contentChildren: (Paragraph | Table)[] = [];

  const blocksWithFigures = placeFiguresInBlocks(blocks, params.sourceImages);

  let pageHasContent = false;
  for (const block of blocksWithFigures) {
    if (block.kind !== 'pagebreak') pageHasContent = true;
    switch (block.kind) {
      // Source page break (see SOURCE_PAGE_MARKER). Keeps the DOCX paginating
      // like the source — and like the PDF built from the same content.
      case 'pagebreak':
        if (pageHasContent) {
          contentChildren.push(new Paragraph({ children: [new PageBreak()] }));
          pageHasContent = false;
        }
        break;

      // A figure carried over from the source PDF.
      case 'figure': {
        const drawn = figureParagraph(block.figure);
        if (drawn) contentChildren.push(drawn);
        break;
      }

      case 'title':
        contentChildren.push(
          new Paragraph({
            heading: HeadingLevel.TITLE,
            spacing: { before: 360, after: 120 },
            children: [
              new TextRun({
                text: block.text.toUpperCase(),
                bold: true,
                size: 36, // 18pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'part':
        contentChildren.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 360, after: 120 },
            children: [
              new TextRun({
                text: block.text,
                bold: true,
                size: 28, // 14pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'section':
        contentChildren.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 360, after: 120 },
            children: [
              new TextRun({
                text: block.text,
                bold: true,
                size: 24, // 12pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'subsection':
        contentChildren.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 200, after: 80 },
            children: [
              new TextRun({
                text: block.text,
                bold: true,
                size: 20, // 10pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'bullet':
        contentChildren.push(
          new Paragraph({
            bullet: { level: 0 },
            spacing: { before: 60, after: 60 },
            indent: { left: convertInchesToTwip(0.33) },
            children: [
              new TextRun({
                text: block.text,
                size: 20, // 10pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'numbered':
        contentChildren.push(
          new Paragraph({
            spacing: { before: 60, after: 60 },
            indent: { left: convertInchesToTwip(0.33) },
            children: [
              new TextRun({
                text: block.text,
                size: 20, // 10pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'callout':
        contentChildren.push(
          new Paragraph({
            spacing: { before: 120, after: 120 },
            indent: { left: convertInchesToTwip(0.17) },
            border: {
              left: { style: BorderStyle.SINGLE, size: 12, color: '7C3AED' },
            },
            children: [
              new TextRun({
                text: block.text,
                bold: true,
                italics: true,
                size: 20, // 10pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'table':
        if (block.tableRows && block.tableRows.length > 0) {
          const numCols = Math.max(...block.tableRows.map(r => r.length));

          const docxRows = block.tableRows.map((row, rowIdx) => {
            const isHeader = rowIdx === 0;
            const cells = [];

            for (let colIdx = 0; colIdx < numCols; colIdx++) {
              const cellText = colIdx < row.length ? row[colIdx] : '';
              cells.push(
                new TableCell({
                  shading: isHeader ? { fill: 'F2F2F2' } : undefined,
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: cellText,
                          bold: isHeader,
                          size: 20, // 10pt
                          font: 'Arial',
                        }),
                      ],
                    }),
                  ],
                })
              );
            }

            return new TableRow({ children: cells });
          });

          contentChildren.push(
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: docxRows,
            })
          );

          // Add spacing after table
          contentChildren.push(
            new Paragraph({ spacing: { before: 120, after: 120 } })
          );
        }
        break;

      case 'paragraph':
      default:
        contentChildren.push(
          new Paragraph({
            spacing: { before: 120, after: 240 },
            children: [
              new TextRun({
                text: block.text,
                size: 20, // 10pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;
    }
  }

  // Create the document with sections
  const doc = new Document({
    title: displayTitle,
    creator: organizationName,
    description: 'Formatted Legal Research Report',
    sections: [
      // Title page section — omitted when the source document has its own.
      ...(params.includeCoverPage === false
        ? []
        : [{
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(0.83),
              right: convertInchesToTwip(0.83),
            },
          },
        },
        children: titlePageChildren,
      }]),
      // Content section with footer
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(0.83),
              right: convertInchesToTwip(0.83),
            },
          },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 200 },
                children: [
                  new TextRun({
                    children: ['Page ', PageNumber.CURRENT],
                    size: 20, // 10pt
                    font: 'Arial',
                    color: '666666',
                  }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: copyrightShort,
                    size: 16, // 8pt
                    font: 'Arial',
                    color: '808080',
                  }),
                ],
              }),
            ],
          }),
        },
        children: contentChildren,
      },
    ],
  });

  // Generate the DOCX blob
  const buffer = await Packer.toBlob(doc);

  // Generate filename (matching PDF naming convention)
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
  };
  const subjectSlug = slugify(params.title || 'formatted-report');
  const dateSlug = createdAt.toISOString().slice(0, 10);
  const filename = `${subjectSlug}-formatted-${dateSlug}.docx`;

  return new File([buffer], filename, {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    lastModified: createdAt.getTime(),
  });
}
